import { getIsQuestWorkflow, getIsQuestInDemoMode } from 'a/hub/selectors';
import { SEARCHLIGHT_EVENT_TYPE } from '../../../lib/SearchlightDispatcher';
import gameIds from '../data/GameIds';

// searchlight dispatch event for reporting
export const metagameSearchlightDispatch = (eventType, details) => (dispatch, getState) => {
  dispatch({
    type: 'SEARCHLIGHT_DISPATCH_EVENT',
    payload: {
      gameId: gameIds.METAGAME,
      application: {
        gameId: gameIds.METAGAME,
        eventType,
        details: {
          isQuestWorkflow: getIsQuestWorkflow(getState()),
          isQuestInDemoMode: getIsQuestInDemoMode(getState()),
          season: getState().questWorkflowReducer.season,
          ...details,
        },
      },
    },
  });
};

export const reportExitGame = (gameId) => (dispatch, getState) => {
  const state = getState();
  const isQuestWorkflow = getIsQuestWorkflow(state);
  const selectedActivity = gameId ?? state.selectedActivity.activity;
  dispatch({
    type: 'SEARCHLIGHT_DISPATCH_EVENT',
    payload: {
      application: {
        gameId: selectedActivity,
        eventType: 'EXIT_GAME',
        details: {
          isQuestWorkflow,
        },
      },
    },
  });
};

// property questName added because it was required before,  questSkillType hardcoded because otherwise it fails
export const reportQuestStart = ({ location, questId }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.QUEST_START, {
    location,
    questId,
    questName: '',
    questSkillType: 'green',
    isQuestWorkflow: true,
  });

// property questName added because it was required before,  questSkillType hardcoded because otherwise it fails
export const reportQuestComplete = ({ location, questId }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.QUEST_COMPLETE, {
    location,
    questId,
    questName: '',
    isQuestWorkflow: true,
  });

/**
 * Reports when a quest game is automatically completed after 3 consecutive failures.
 *
 * @function reportQuestStepAutoCompleteOn3Failures
 * @param {Object} params - The parameters for the reporting event
 * @param {string} params.questId - The unique identifier of the quest containing the failed step
 * @param {string} params.autoCompletedGame - The unique identifier of the game that was auto-completed
 * @param {string} params.failedLevel - The level that the user failed 3 times
 * @param {string} params.season - The current season identifier when the auto-complete occurred
 * @returns {Function} A Redux thunk action that dispatches the QUEST_STEP_AUTOCOMPLETE_ON_THREE_FAILURES event
 *
 * @example
 * Report auto-completion after 3 failures on level 7 of Unmask That game in Workshop (season 3)
 * dispatch(reportQuestStepAutoCompleteOn3Failures({
 *   questId: "302",
 *   autoCompletedGame: "unmask_that",
 *   failedLevel: "6",
 *   season: "3"
 * }));
 */
export const reportQuestStepAutoCompleteOn3Failures = ({ questId, autoCompletedGame, failedLevel, season }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.QUEST_STEP_AUTOCOMPLETE_ON_THREE_FAILURES, {
    questId,
    autoCompletedGame,
    failedLevel,
    season,
  });

export const reportQuestStepComplete = ({ location, questId, stepId }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.QUEST_STEP_COMPLETE, {
    location,
    questId,
    stepId,
    questName: '',
    isQuestWorkflow: true,
  });

export const reportQuestStepFailure = ({ questId, gameName, levelFailed }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.QUEST_STEP_FAILURE, {
    questId,
    gameName,
    levelFailed,
    isQuestWorkflow: true,
  });

export const reportRewardEarned = ({ rewardType, rewardCount, rewardInfo }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.REWARD_EARNED, {
    rewardType,
    rewardCount,
    rewardInfo,
    isQuestWorkflow: true, // rewarding always happens from quests
  });

export const reportRewardSpent = ({ rewardType, selection, selectionType, selectionShop }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.REWARD_SPENT, {
    rewardType,
    selection,
    selectionType,
    selectionShop,
  });

export const reportAdminUsage = ({ action, selection = {} }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.ADMIN_DEBUG_EVENT, {
    action,
    selection,
  });

export const reportCuriosoSelected = ({ selection = {} }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.CURIOSO_SELECTION, {
    selection,
  });

export const reportRedirect = ({ from, to, questId, location }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.REDIRECT, {
    from,
    to,
    questId,
    location,
  });

export const reportNavigation = ({ destination, destinationGroup = '', origin = '', trigger = '', triggerType = '' }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.NAVIGATION, {
    destination,
    destinationGroup,
    origin,
    trigger,
    triggerType,
  });

export const reportUserSettingsChanged = ({ key, value }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.SETTING, {
    key,
    value,
  });

export const reportSideQuestStart = ({ selectedGame, levels }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.SIDE_QUEST_START, {
    selectedGame,
    levels,
    isQuestWorkflow: true,
  });

export const reportSideQuestEnd = ({ selectedGame = '', levels = [], isSuccess, skipped }) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.SIDE_QUEST_END, {
    selectedGame,
    levels,
    isSuccess,
    skipped,
    isQuestWorkflow: true,
  });

export const reportAdminEnabledOnProd = () =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.ADMIN_ENABLED_ON_PROD, {});

export const reportProfileRequest = (gameContentSets, success, metadata) =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.PROFILE_REQUEST, {
    gameContentSets,
    success,
    metadata,
  });

// game can be disabled and once this game appears in quest we should autocomplete it and notify about this event
export const reportQuestStepAutoComplete = () => (dispatch, getState) => {
  if (getState().questWorkflowReducer.autoCompletedGame) {
    dispatch(
      metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.QUEST_STEP_AUTOCOMPLETE, {
        isQuestWorkflow: true,
        isQuestInDemoMode: getIsQuestInDemoMode(getState()),
        season: getState().questWorkflowReducer.season.toString(),
        questId: getState().questWorkflowReducer.currentActiveQuestId.toString(),
        autoCompletedGame: getState().questWorkflowReducer.autoCompletedGame,
      }));
  }
};

export const reportDemoLaunched = demoSeason =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.LAUNCH_DEMO, {
    demoSeason,
  });

export const reportDemoQuit = demoSeason =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.QUIT_DEMO, {
    demoSeason,
  });

export const reportDemoReset = demoSeason =>
  metagameSearchlightDispatch(SEARCHLIGHT_EVENT_TYPE.RESET_DEMO, {
    demoSeason,
  });
